import React from 'react';
import { motion } from 'framer-motion';
import { KeyboardLetterState, LetterStatus } from '../../types/paydle';

interface KeyboardProps {
  onKeyPress: (key: string) => void;
  letterStates: KeyboardLetterState;
  disabled?: boolean;
}

const keyRows = [
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
  ['ENTER', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', 'BACK'],
];

const getKeyColor = (status?: LetterStatus): string => {
  switch (status) {
    case LetterStatus.CORRECT:
      return 'bg-green-500 hover:bg-green-600 text-white border-green-600';
    case LetterStatus.PRESENT:
      return 'bg-yellow-500 hover:bg-yellow-600 text-white border-yellow-600';
    case LetterStatus.ABSENT:
      return 'bg-gray-500 hover:bg-gray-600 text-white border-gray-600';
    default:
      return 'bg-gray-200 hover:bg-gray-300 text-gray-800 border-gray-300';
  }
};

const Key: React.FC<{
  keyValue: string;
  status?: LetterStatus;
  onPress: (key: string) => void;
  disabled: boolean;
}> = ({ keyValue, status, onPress, disabled }) => {
  const isSpecial = keyValue === 'ENTER' || keyValue === 'BACK';
  const color = getKeyColor(status);

  return (
    <motion.button
      whileTap={{ scale: disabled ? 1 : 0.95 }}
      onClick={() => !disabled && onPress(keyValue)}
      disabled={disabled}
      className={`${color} ${
        isSpecial ? 'px-4 sm:px-6 text-xs sm:text-sm' : 'text-sm sm:text-lg'
      } py-3 sm:py-4 rounded font-bold border-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-1 min-w-0 ${
        isSpecial ? 'flex-grow-0' : ''
      }`}
      style={{
        minWidth: isSpecial ? '60px' : '30px',
        maxWidth: isSpecial ? '80px' : '50px',
      }}
    >
      {keyValue === 'BACK' ? '⌫' : keyValue}
    </motion.button>
  );
};

export const Keyboard: React.FC<KeyboardProps> = ({ onKeyPress, letterStates, disabled = false }) => {
  return (
    <div className="w-full max-w-xl mx-auto px-2 pb-4">
      <div className="flex flex-col gap-1 sm:gap-2">
        {keyRows.map((row, rowIndex) => (
          <div key={rowIndex} className="flex gap-1 sm:gap-2 justify-center">
            {row.map(key => (
              <Key
                key={key}
                keyValue={key}
                status={letterStates[key]}
                onPress={onKeyPress}
                disabled={disabled}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
